/**
 * 🎯 useWellAssignments Hook
 * 
 * Task #76: Core data hook for well assignment management
 * 
 * Provides reactive access to well assignments with:
 * - Automatic visibility mode filtering
 * - Manager vs pumper role awareness
 * - Assignment/unassignment operations
 * - Bulk operations support
 * - History tracking
 * - Optimistic updates
 * - Error handling with recovery
 * - Offline-aware caching
 * 
 * @module useWellAssignments
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCompanyId, getCompanyStorageKey } from '../utils/tenantContext';

// Service imports
import {
  // Settings
  getWellVisibilitySettings,
  setWellVisibilitySettings,
  // Assignment operations
  assignWellToPumper,
  unassignWellFromPumper,
  bulkAssignWells,
  assignRouteToPumper,
  // Queries
  getWellsForPumper,
  getAssignmentHistory,
  // Types
  type AssignmentResult,
  type BulkAssignmentResult,
  type AssignmentOptions,
  type UnassignmentOptions,
  type WellForAssignment,
  type PumperForAssignment,
} from '../utils/services/wellAssignmentService';

import type {
  WellVisibilitySettings,
  AssignmentHistoryEntry,
} from '../types/wellAssignment';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Assignment status for UI display
 */
export type AssignmentStatus = 
  | 'assigned'           // Has primary pumper
  | 'unassigned'         // No pumper (orphan)
  | 'backup_only'        // Only backup assigned
  | 'temporary_coverage' // Under temporary coverage
  | 'backup_active';     // Backup is currently active

/**
 * Well with assignment info for UI
 */
export interface WellAssignmentInfo {
  id: string;
  name: string;
  status: string; // well status (active, inactive, etc.)
  assignmentStatus: AssignmentStatus;
  primaryPumperId: string | null;
  primaryPumperName: string | null;
  backupPumperId: string | null;
  backupPumperName: string | null;
  backupActive: boolean;
  assignedPumperIds: string[];
  temporaryAssignment?: {
    active: boolean;
    coveringPumperId: string;
    coveringPumperName: string;
    endDate?: string;
  };
  lastAssignmentChange?: Date;
  latitude?: number;
  longitude?: number;
  routePriority?: number;
}

/**
 * Pumper summary for assignment UI
 */
export interface PumperAssignmentSummary {
  id: string;
  name: string;
  email?: string;
  role: string;
  wellCount: number;
  activeBackupCount: number;
  temporaryCoverageCount: number;
}

/**
 * Hook options
 */
export interface UseWellAssignmentsOptions {
  /** Current user for role-based filtering */
  user: {
    id: string;
    role: string;
    companyId?: string;
  } | null;
  /** Auto-refresh interval in ms (0 = disabled) */
  refreshInterval?: number;
  /** Include history in initial load */
  includeHistory?: boolean;
  /** Filter to specific pumper's wells */
  pumperId?: string;
}

/**
 * Hook state
 */
export interface UseWellAssignmentsState {
  // Data
  wells: WellAssignmentInfo[];
  orphanWells: WellAssignmentInfo[];
  settings: WellVisibilitySettings | null;
  history: AssignmentHistoryEntry[];
  
  // UI State
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
  
  // Derived counts
  totalWells: number;
  assignedCount: number;
  unassignedCount: number;
  backupActiveCount: number;
  temporaryCoverageCount: number;
}

/**
 * Hook actions
 */
export interface UseWellAssignmentsActions {
  // Queries
  refresh: () => Promise<void>;
  loadHistory: (wellId: string, limit?: number) => Promise<AssignmentHistoryEntry[]>;
  
  // Settings
  updateSettings: (settings: Partial<WellVisibilitySettings>) => Promise<void>;
  
  // Single assignment
  assignWell: (
    wellId: string,
    wellName: string,
    pumper: PumperForAssignment,
    options?: Partial<AssignmentOptions>
  ) => Promise<AssignmentResult>;
  
  unassignWell: (
    wellId: string,
    wellName: string,
    pumperId: string | null,
    options?: Partial<UnassignmentOptions>
  ) => Promise<AssignmentResult>;
  
  // Bulk operations
  bulkAssign: (
    wells: WellForAssignment[],
    pumper: PumperForAssignment,
    options?: Partial<AssignmentOptions>
  ) => Promise<BulkAssignmentResult>;
  
  assignRoute: (
    routeId: string,
    routeName: string,
    wellIds: string[],
    pumper: PumperForAssignment,
    options?: Partial<AssignmentOptions>
  ) => Promise<BulkAssignmentResult>;
  
  // Selectors
  getWellById: (wellId: string) => WellAssignmentInfo | undefined;
  getWellsForPumper: (pumperId: string) => WellAssignmentInfo[];
  getUnassignedWells: () => WellAssignmentInfo[];
  getWellsWithActiveBackup: () => WellAssignmentInfo[];
  getWellsWithTemporaryCoverage: () => WellAssignmentInfo[];
  
  // Filters
  filterByStatus: (status: AssignmentStatus) => WellAssignmentInfo[];
  filterByPumper: (pumperId: string) => WellAssignmentInfo[];
  searchWells: (query: string) => WellAssignmentInfo[];
}

/**
 * Full hook return type
 */
export type UseWellAssignmentsReturn = UseWellAssignmentsState & UseWellAssignmentsActions;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Determine assignment status from well data
 */
function getAssignmentStatus(well: Partial<WellForAssignment> & {
  temporaryAssignment?: { active?: boolean };
  backupActive?: boolean;
  backupPumperId?: string;
}): AssignmentStatus {
  // Check temporary coverage first
  if (well.temporaryAssignment?.active) {
    return 'temporary_coverage';
  }
  
  // Check backup active
  if (well.backupActive && well.backupPumperId) {
    return 'backup_active';
  }
  
  // Check primary assignment
  if (well.primaryPumperId || ((well.assignedPumperIds?.length ?? 0) > 0)) {
    return 'assigned';
  }
  
  // Check backup only
  if (well.backupPumperId && !well.primaryPumperId) {
    return 'backup_only';
  }
  
  return 'unassigned';
}

/**
 * Transform raw well data to WellAssignmentInfo
 */
function transformWellToAssignmentInfo(well: Partial<WellForAssignment> & {
  temporaryAssignment?: {
    active?: boolean;
    coveringPumperId?: string;
    coveringPumperName?: string;
    endDate?: string;
  };
  backupActive?: boolean;
  lastAssignmentChange?: string | Date;
  latitude?: number;
  longitude?: number;
  routePriority?: number;
  priority?: number;
}): WellAssignmentInfo {
  const wellId = well.id ?? 'unknown';
  return {
    id: wellId,
    name: well.name || wellId,
    status: well.status || 'active',
    assignmentStatus: getAssignmentStatus(well),
    primaryPumperId: well.primaryPumperId || well.assignedPumperIds?.[0] || null,
    primaryPumperName: well.primaryPumperName || null,
    backupPumperId: well.backupPumperId || null,
    backupPumperName: well.backupPumperName || null,
    backupActive: well.backupActive || false,
    assignedPumperIds: well.assignedPumperIds || [],
    temporaryAssignment: (well.temporaryAssignment?.active && well.temporaryAssignment.coveringPumperId) ? {
      active: true,
      coveringPumperId: well.temporaryAssignment.coveringPumperId,
      coveringPumperName: well.temporaryAssignment.coveringPumperName || 'Unknown',
      endDate: well.temporaryAssignment.endDate,
    } : undefined,
    lastAssignmentChange: well.lastAssignmentChange 
      ? new Date(well.lastAssignmentChange) 
      : undefined,
    latitude: well.latitude,
    longitude: well.longitude,
    routePriority: well.routePriority || well.priority,
  };
}

/**
 * Cache key for localStorage
 */
const CACHE_KEY = 'wellAssignments';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * useWellAssignments - Core hook for well assignment management
 * 
 * @param options - Hook configuration options
 * @returns State and actions for managing well assignments
 * 
 * @example
 * ```tsx
 * function WellAssignmentManager() {
 *   const { user } = useAuth();
 *   const {
 *     wells,
 *     orphanWells,
 *     loading,
 *     assignWell,
 *     getUnassignedWells,
 *     filterByPumper,
 *   } = useWellAssignments({ user });
 * 
 *   if (loading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       {orphanWells.length > 0 && (
 *         <Alert>⚠️ {orphanWells.length} unassigned wells</Alert>
 *       )}
 *       <WellList wells={wells} onAssign={assignWell} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useWellAssignments(
  options: UseWellAssignmentsOptions
): UseWellAssignmentsReturn {
  const { user, refreshInterval = 0, includeHistory = false, pumperId } = options;
  
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [wells, setWells] = useState<WellAssignmentInfo[]>([]);
  const [orphanWells, setOrphanWells] = useState<WellAssignmentInfo[]>([]);
  const [settings, setSettings] = useState<WellVisibilitySettings | null>(null);
  const [history, setHistory] = useState<AssignmentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Refs for cleanup and deduplication
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DERIVED STATE (memoized)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const derivedCounts = useMemo(() => {
    const assigned = wells.filter(w => w.assignmentStatus === 'assigned').length;
    const unassigned = wells.filter(w => w.assignmentStatus === 'unassigned').length;
    const backupActive = wells.filter(w => w.assignmentStatus === 'backup_active').length;
    const temporaryCoverage = wells.filter(w => w.assignmentStatus === 'temporary_coverage').length;
    
    return {
      totalWells: wells.length,
      assignedCount: assigned,
      unassignedCount: unassigned,
      backupActiveCount: backupActive,
      temporaryCoverageCount: temporaryCoverage,
    };
  }, [wells]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // ROLE HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const isManager = useMemo(() => {
    if (!user) return false;
    return ['admin', 'manager', 'superadmin'].includes(user.role);
  }, [user?.role]);
  
  const isPumper = useMemo(() => {
    if (!user) return false;
    return user.role === 'pumper';
  }, [user?.role]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // CACHE HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(getCompanyStorageKey(CACHE_KEY));
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      
      if (age > CACHE_TTL) {
        localStorage.removeItem(getCompanyStorageKey(CACHE_KEY));
        return null;
      }
      
      return data as WellAssignmentInfo[];
    } catch {
      return null;
    }
  }, []);
  
  const setCachedData = useCallback((data: WellAssignmentInfo[]) => {
    try {
      localStorage.setItem(
        getCompanyStorageKey(CACHE_KEY),
        JSON.stringify({ data, timestamp: Date.now() })
      );
    } catch {
      // Storage full or unavailable
    }
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const loadData = useCallback(async (showLoading = true) => {
    // Prevent duplicate loads
    if (loadingRef.current) return;
    if (!user) {
      setWells([]);
      setOrphanWells([]);
      setLoading(false);
      return;
    }
    
    loadingRef.current = true;
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const companyId = getCompanyId();
      if (!companyId || companyId === 'default') {
        throw new Error('Invalid company context');
      }
      
      // Load settings first
      const visibilitySettings = await getWellVisibilitySettings();
      if (mountedRef.current) {
        setSettings(visibilitySettings);
      }
      
      // Determine which wells to load based on role
      let wellsData: WellForAssignment[];
      
      if (isManager) {
        // Managers see all wells
        // Use the Firebase query directly for complete picture
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../utils/firebase/client');
        const { FIRESTORE_PATHS } = await import('../utils/firebase/paths');
        
        const wellsPath = FIRESTORE_PATHS.wells(companyId);
        const q = query(
          collection(db, wellsPath),
          where('companyId', '==', companyId)
        );
        
        const snapshot = await getDocs(q);
        wellsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as WellForAssignment));
        
      } else if (isPumper && user?.id) {
        // Pumpers see based on visibility mode
        const effectivePumperId = pumperId || user.id;
        
        if (visibilitySettings.mode === 'all') {
          // All wells visible (read-only for non-assigned)
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../utils/firebase/client');
          const { FIRESTORE_PATHS } = await import('../utils/firebase/paths');
          
          const wellsPath = FIRESTORE_PATHS.wells(companyId);
          const q = query(
            collection(db, wellsPath),
            where('companyId', '==', companyId)
          );
          
          const snapshot = await getDocs(q);
          wellsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as WellForAssignment));
        } else {
          // Only assigned wells
          wellsData = await getWellsForPumper(effectivePumperId);
        }
      } else {
        wellsData = [];
      }
      
      // Transform to UI format
      const transformedWells = wellsData.map(transformWellToAssignmentInfo);
      
      // Separate orphans
      const orphans = transformedWells.filter(w => w.assignmentStatus === 'unassigned');
      
      if (mountedRef.current) {
        setWells(transformedWells);
        setOrphanWells(orphans);
        setLastRefresh(new Date());
        setCachedData(transformedWells);
      }
      
      // Load history if requested
      if (includeHistory && transformedWells.length > 0) {
        // Load history for first few wells as sample
        const historyPromises = transformedWells.slice(0, 5).map(w => 
          getAssignmentHistory(w.id, { limit: 10 })
        );
        const historyResults = await Promise.all(historyPromises);
        const allHistory = historyResults.flat().sort(
          (a, b) => {
            const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : (a.timestamp as { seconds: number }).seconds * 1000;
            const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : (b.timestamp as { seconds: number }).seconds * 1000;
            return timeB - timeA;
          }
        );
        
        if (mountedRef.current) {
          setHistory(allHistory.slice(0, 50));
        }
      }
      
    } catch (err: unknown) {
      console.error('❌ Failed to load well assignments:', err);
      
      // Try cache fallback
      const cached = getCachedData();
      if (cached && mountedRef.current) {
        setWells(cached);
        setOrphanWells(cached.filter(w => w.assignmentStatus === 'unassigned'));
        setError('Using cached data - network unavailable');
      } else if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load well assignments');
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, isManager, isPumper, pumperId, includeHistory, getCachedData, setCachedData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    loadData();
    
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [loadData]);
  
  // Auto-refresh
  useEffect(() => {
    if (refreshInterval > 0) {
      refreshTimerRef.current = setInterval(() => {
        loadData(false); // Silent refresh
      }, refreshInterval);
      
      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
        }
      };
    }
  }, [refreshInterval, loadData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const refresh = useCallback(async () => {
    await loadData(true);
  }, [loadData]);
  
  const loadHistoryForWell = useCallback(async (
    wellId: string,
    limit = 20
  ): Promise<AssignmentHistoryEntry[]> => {
    try {
      return await getAssignmentHistory(wellId, { limit });
    } catch (err: unknown) {
      console.error('Failed to load history:', err);
      return [];
    }
  }, []);
  
  const updateSettings = useCallback(async (
    newSettings: Partial<WellVisibilitySettings>
  ) => {
    if (!settings || !user) return;
    
    try {
      await setWellVisibilitySettings(newSettings, user.id);
      setSettings({ ...settings, ...newSettings });
      
      // Refresh wells with new visibility mode
      await loadData(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update settings';
      setError(message);
      throw err;
    }
  }, [settings, user, loadData]);
  
  const assignWell = useCallback(async (
    wellId: string,
    wellName: string,
    pumper: PumperForAssignment,
    assignOptions: Partial<AssignmentOptions> = {}
  ): Promise<AssignmentResult> => {
    try {
      const result = await assignWellToPumper(wellId, wellName, pumper, {
        performedBy: user?.id || 'unknown',
        performedByName: user?.role || 'System',
        ...assignOptions,
      });
      
      if (result.success) {
        // Optimistic update
        setWells(prev => prev.map(w => 
          w.id === wellId
            ? {
                ...w,
                assignmentStatus: 'assigned' as AssignmentStatus,
                primaryPumperId: pumper.id,
                primaryPumperName: pumper.name,
                assignedPumperIds: [pumper.id, ...(w.backupPumperId ? [w.backupPumperId] : [])],
                lastAssignmentChange: new Date(),
              }
            : w
        ));
        
        // Update orphan list
        setOrphanWells(prev => prev.filter(w => w.id !== wellId));
      }
      
      return result;
    } catch (err: unknown) {
      const result: AssignmentResult = {
        success: false,
        wellId,
        wellName,
        error: err instanceof Error ? err.message : 'Assignment failed',
      };
      return result;
    }
  }, [user]);
  
  const unassignWell = useCallback(async (
    wellId: string,
    wellName: string,
    pumperId: string | null,
    unassignOptions: Partial<UnassignmentOptions> = {}
  ): Promise<AssignmentResult> => {
    try {
      const result = await unassignWellFromPumper(wellId, wellName, pumperId, {
        performedBy: user?.id || 'unknown',
        performedByName: user?.role || 'System',
        ...unassignOptions,
      });
      
      if (result.success) {
        // Optimistic update
        setWells(prev => prev.map(w => 
          w.id === wellId
            ? {
                ...w,
                assignmentStatus: 'unassigned' as AssignmentStatus,
                primaryPumperId: null,
                primaryPumperName: null,
                assignedPumperIds: w.backupPumperId ? [w.backupPumperId] : [],
                lastAssignmentChange: new Date(),
              }
            : w
        ));
        
        // Add to orphan list
        const unassignedWell = wells.find(w => w.id === wellId);
        if (unassignedWell) {
          setOrphanWells(prev => [...prev, { ...unassignedWell, assignmentStatus: 'unassigned' }]);
        }
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        wellId,
        wellName,
        error: err instanceof Error ? err.message : 'Unassignment failed',
      };
    }
  }, [user, wells]);
  
  const bulkAssign = useCallback(async (
    wellsToAssign: WellForAssignment[],
    pumper: PumperForAssignment,
    assignOptions: Partial<AssignmentOptions> = {}
  ): Promise<BulkAssignmentResult> => {
    try {
      const result = await bulkAssignWells(wellsToAssign, pumper, {
        performedBy: user?.id || 'unknown',
        performedByName: user?.role || 'System',
        ...assignOptions,
      });
      
      // Refresh to get accurate state after bulk operation
      await loadData(false);
      
      return result;
    } catch (err: unknown) {
      const errMessage = err instanceof Error ? err.message : 'Bulk assignment failed';
      return {
        success: false,
        totalWells: wellsToAssign.length,
        successCount: 0,
        failureCount: wellsToAssign.length,
        results: [],
        failures: wellsToAssign.map(w => ({
          success: false,
          wellId: w.id,
          wellName: w.name,
          error: errMessage,
        })),
      };
    }
  }, [user, loadData]);
  
  const assignRoute = useCallback(async (
    routeId: string,
    _routeName: string, // Note: route name is fetched inside service
    _wellIds: string[], // Note: well IDs are fetched from route document
    pumper: PumperForAssignment,
    assignOptions: Partial<AssignmentOptions> = {}
  ): Promise<BulkAssignmentResult> => {
    try {
      const result = await assignRouteToPumper(routeId, pumper, {
        performedBy: user?.id || 'unknown',
        performedByName: user?.role || 'System',
        ...assignOptions,
      });
      
      // Refresh to get accurate state
      await loadData(false);
      
      return result;
    } catch {
      // Route assignment failed - return empty failure result
      return {
        success: false,
        totalWells: _wellIds.length,
        successCount: 0,
        failureCount: _wellIds.length,
        results: [],
        failures: [],
      };
    }
  }, [user, loadData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // SELECTORS (memoized)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getWellById = useCallback((wellId: string): WellAssignmentInfo | undefined => {
    return wells.find(w => w.id === wellId);
  }, [wells]);
  
  const getWellsForPumperSelector = useCallback((targetPumperId: string): WellAssignmentInfo[] => {
    return wells.filter(w => 
      w.primaryPumperId === targetPumperId ||
      w.assignedPumperIds.includes(targetPumperId) ||
      w.temporaryAssignment?.coveringPumperId === targetPumperId
    );
  }, [wells]);
  
  const getUnassignedWells = useCallback((): WellAssignmentInfo[] => {
    return wells.filter(w => w.assignmentStatus === 'unassigned');
  }, [wells]);
  
  const getWellsWithActiveBackup = useCallback((): WellAssignmentInfo[] => {
    return wells.filter(w => w.assignmentStatus === 'backup_active');
  }, [wells]);
  
  const getWellsWithTemporaryCoverage = useCallback((): WellAssignmentInfo[] => {
    return wells.filter(w => w.assignmentStatus === 'temporary_coverage');
  }, [wells]);
  
  const filterByStatus = useCallback((status: AssignmentStatus): WellAssignmentInfo[] => {
    return wells.filter(w => w.assignmentStatus === status);
  }, [wells]);
  
  const filterByPumper = useCallback((targetPumperId: string): WellAssignmentInfo[] => {
    return wells.filter(w => 
      w.primaryPumperId === targetPumperId ||
      w.backupPumperId === targetPumperId ||
      w.temporaryAssignment?.coveringPumperId === targetPumperId
    );
  }, [wells]);
  
  const searchWells = useCallback((query: string): WellAssignmentInfo[] => {
    const lowerQuery = query.toLowerCase();
    return wells.filter(w =>
      w.name.toLowerCase().includes(lowerQuery) ||
      w.id.toLowerCase().includes(lowerQuery) ||
      w.primaryPumperName?.toLowerCase().includes(lowerQuery)
    );
  }, [wells]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  
  return {
    // State
    wells,
    orphanWells,
    settings,
    history,
    loading,
    error,
    lastRefresh,
    
    // Derived counts
    ...derivedCounts,
    
    // Actions
    refresh,
    loadHistory: loadHistoryForWell,
    updateSettings,
    assignWell,
    unassignWell,
    bulkAssign,
    assignRoute,
    
    // Selectors
    getWellById,
    getWellsForPumper: getWellsForPumperSelector,
    getUnassignedWells,
    getWellsWithActiveBackup,
    getWellsWithTemporaryCoverage,
    filterByStatus,
    filterByPumper,
    searchWells,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Lightweight selector hook for just orphan wells
 */
export function useOrphanWellCount(user: UseWellAssignmentsOptions['user']): number {
  const { unassignedCount } = useWellAssignments({ user });
  return unassignedCount;
}

/**
 * Check if current user can assign wells
 */
export function useCanAssignWells(user: UseWellAssignmentsOptions['user']): boolean {
  if (!user) return false;
  return ['admin', 'manager', 'superadmin'].includes(user.role);
}

export default useWellAssignments;
